import { CSSProperties, FocusEvent, MutableRefObject, ReactNode, UIEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, PanInfo, useDragControls, useReducedMotion } from "framer-motion";
import { FloatingFocusManager, FloatingOverlay, FloatingPortal, useDismiss, useFloating, useInteractions, useMergeRefs } from "@floating-ui/react";
import { useTranslation } from "react-i18next";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { useVisualViewport } from "core/hooks/useVisualViewport";

export const SHEET_ANIMATION_DURATION_S = 0.3;
export const KEYBOARD_SETTLE_DELAY_MS = 300;
export const SWIPE_CLOSE_OFFSET_PX = 100;
export const SWIPE_CLOSE_VELOCITY = 500;

const TOP_STRIP_PX = 24;
const RESTING_MAX_HEIGHT = "70%";
const CONTENT_BOTTOM_PADDING_PX = 24;
const FIELD_SCROLL_MARGIN_PX = 24;
const FIELD_SELECTOR = "input, textarea, select, [contenteditable='true']";

type BottomSheetProps = {
  isOpen: boolean;
  title?: string;
  onClose?: () => void;
  dismissible?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
  initialFocus?: MutableRefObject<HTMLElement | null>;
  children?: ReactNode;
};

const revealFieldAndActions = (container: HTMLElement, field: HTMLElement) => {
  const fieldTop = field.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
  const maxScrollTop = container.scrollHeight - container.clientHeight;
  container.scrollTop = Math.max(0, Math.min(maxScrollTop, fieldTop - FIELD_SCROLL_MARGIN_PX));
};

const resetOverlayScroll = (e: UIEvent<HTMLDivElement>) => {
  e.currentTarget.scrollTop = 0;
  e.currentTarget.scrollLeft = 0;
};

const isField = (target: EventTarget | null): target is HTMLElement => target instanceof HTMLElement && target.matches(FIELD_SELECTOR);

const topInset = (inset: number | undefined) => (typeof inset === "number" ? `${inset}px` : "env(safe-area-inset-top, 0px)");

export const BottomSheet = ({ isOpen, title, onClose, dismissible = true, header, footer, initialFocus, children }: BottomSheetProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const reduceMotion = useReducedMotion();
  const dragControls = useDragControls();
  const { insetPadding } = useMobilePlatform();
  const viewport = useVisualViewport(isOpen);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const focusTimer = useRef<ReturnType<typeof setTimeout>>();
  const wasDragged = useRef(false);
  const [isEditing, setIsEditing] = useState(false);

  const close = useCallback(() => onClose?.(), [onClose]);

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!open) close();
    },
  });
  const dismiss = useDismiss(context, { enabled: dismissible, escapeKey: dismissible, outsidePress: dismissible });
  const { getFloatingProps } = useInteractions([dismiss]);
  const mergedSheetRef = useMergeRefs([refs.setFloating, sheetRef]);

  const keepFocusedFieldVisible = useCallback(() => {
    const container = scrollRef.current;
    const active = document.activeElement;
    if (container && isField(active) && container.contains(active)) {
      revealFieldAndActions(container, active);
    }
  }, []);

  useEffect(() => {
    keepFocusedFieldVisible();
  }, [viewport, keepFocusedFieldVisible]);

  useEffect(() => () => clearTimeout(focusTimer.current), []);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
    const sheet = sheetRef.current;
    if (sheet && !sheet.contains(document.activeElement)) {
      sheet.focus({ preventScroll: true });
    }
    setIsEditing(isField(document.activeElement) && !!scrollRef.current?.contains(document.activeElement));
  }, [title]);

  const handleFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (!isField(e.target)) {
      return;
    }
    setIsEditing(true);
    clearTimeout(focusTimer.current);
    focusTimer.current = setTimeout(keepFocusedFieldVisible, KEYBOARD_SETTLE_DELAY_MS);
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!(isField(e.relatedTarget) && e.currentTarget.contains(e.relatedTarget))) {
      setIsEditing(false);
    }
  };

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > SWIPE_CLOSE_OFFSET_PX || info.velocity.y > SWIPE_CLOSE_VELOCITY) {
      close();
    }
  };

  const handleHandleClick = () => {
    if (wasDragged.current) {
      wasDragged.current = false;
      return;
    }
    close();
  };

  const overlayStyle: CSSProperties = viewport
    ? { top: viewport.offsetTop, height: viewport.height, bottom: "auto", overflow: "clip" }
    : { top: 0, bottom: 0, height: "100dvh", overflow: "clip" };

  const sheetStyle: CSSProperties = {
    maxHeight: isEditing ? `calc(100% - ${topInset(insetPadding?.top)} - ${TOP_STRIP_PX}px)` : RESTING_MAX_HEIGHT,
  };

  const contentStyle: CSSProperties = {
    paddingBottom:
      typeof insetPadding?.bottom === "number"
        ? insetPadding.bottom + CONTENT_BOTTOM_PADDING_PX
        : `calc(env(safe-area-inset-bottom, 0px) + ${CONTENT_BOTTOM_PADDING_PX}px)`,
    WebkitOverflowScrolling: "touch",
  };

  const transition = { duration: reduceMotion ? 0 : SHEET_ANIMATION_DURATION_S, ease: "easeOut" as const };
  const sheetMotion = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : { initial: { y: "100%" }, animate: { y: 0 }, exit: { y: "100%" } };

  return (
    <AnimatePresence>
      {isOpen && (
        <FloatingPortal key="bottom-sheet">
          <FloatingOverlay
            lockScroll
            data-testid="bottom-sheet-overlay"
            className="z-MODAL flex flex-col justify-end"
            style={overlayStyle}
            onScroll={resetOverlayScroll}
          >
            <motion.div
              data-testid="bottom-sheet-backdrop"
              className="absolute inset-0 bg-[#0f334d99]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transition}
            />
            <FloatingFocusManager context={context} modal initialFocus={initialFocus ?? sheetRef}>
              <motion.div
                ref={mergedSheetRef}
                {...getFloatingProps()}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                tabIndex={-1}
                data-testid="bottom-sheet"
                className="relative flex w-full flex-col self-center rounded-t-[20px] bg-white shadow-2xl outline-none lg:max-w-[600px]"
                style={sheetStyle}
                {...sheetMotion}
                transition={transition}
                drag={dismissible ? "y" : false}
                dragListener={false}
                dragControls={dragControls}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 1 }}
                onDragStart={() => {
                  wasDragged.current = true;
                }}
                onDragEnd={handleDragEnd}
              >
                {dismissible && (
                  <button
                    type="button"
                    data-testid="bottom-sheet-handle"
                    aria-label={t("modals.dismiss")}
                    className="flex w-full shrink-0 justify-center pb-2 pt-3"
                    style={{ touchAction: "none" }}
                    onPointerDown={(e) => {
                      wasDragged.current = false;
                      dragControls.start(e);
                    }}
                    onClick={handleHandleClick}
                  >
                    <span className="h-1 w-10 rounded-full bg-brand-grey-light" />
                  </button>
                )}
                <div
                  ref={scrollRef}
                  data-testid="bottom-sheet-content"
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-8"
                  style={contentStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  {header && <div className="mb-4 flex justify-center">{header}</div>}
                  {title && (
                    <h2 id={titleId} className="mb-4 text-center">
                      {title}
                    </h2>
                  )}
                  {children}
                  {footer && <div className="mt-8 space-y-2">{footer}</div>}
                </div>
              </motion.div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </AnimatePresence>
  );
};
